import React from 'react';
import { Card, Box, Stack, Group, Avatar, Text } from '@mantine/core';
import { IoPlay } from 'react-icons/io5';
import { FEED_ACTION_BAR_SX, FEED_CARD_HOVER, FEED_VIDEO_MEDIA_SX } from './feedCardTheme';

export interface SlopDiscoverCardProps {
  posterUrl: string;
  title: string;
  userName: string;
  userAvatar?: string | null;
  linkedCaption?: string;
  timeLabel: string;
  onOpen?: () => void;
  /** Like / comment / share controls and count labels. */
  actionBar?: React.ReactNode;
}

/**
 * Presentational Slop post card for the mixed discover feed (vertical video + actions).
 * Host wires likes, comments, routes, and moderation.
 */
export function SlopDiscoverCard ({
  posterUrl,
  title,
  userName,
  userAvatar,
  linkedCaption,
  timeLabel,
  onOpen,
  actionBar
}: SlopDiscoverCardProps) {
  return (
    <Card
      padding={0}
      radius="md"
      withBorder
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderColor: 'var(--gs-border)',
        backgroundColor: 'var(--gs-bg-2)',
        ...FEED_CARD_HOVER,
        '@media (max-width: 575px)': {
          borderRadius: 0,
          borderLeft: 'none',
          borderRight: 'none'
        }
      }}
    >
      <Box
        role={onOpen ? 'button' : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onClick={onOpen}
        onKeyDown={
          onOpen
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen();
                }
              }
            : undefined
        }
        sx={{
          position: 'relative',
          cursor: onOpen ? 'pointer' : undefined,
          overflow: 'hidden',
          ...FEED_VIDEO_MEDIA_SX,
          '@media (min-width: 769px)': {
            '&:hover .feed-post-play': {
              transform: 'translate(-50%, -50%) scale(1.08)',
              backgroundColor: 'rgba(0, 0, 0, 0.72)'
            }
          }
        }}
      >
        <img
          src={posterUrl}
          alt={title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {onOpen && (
          <Box
            className="feed-post-play"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s ease, background 0.2s ease'
            }}
          >
            <IoPlay size={28} color="white" />
          </Box>
        )}
      </Box>

      <Box sx={{ padding: '12px 14px', borderTop: '1px solid var(--gs-border)' }}>
        <Group spacing="sm" align="flex-start" noWrap>
          <Avatar src={userAvatar ?? undefined} alt={userName} size={32} radius="xl" />
          <Stack spacing={4} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600} lineClamp={1}>
              {userName}
            </Text>
            <Text size="sm" lineClamp={2} c="dimmed">
              {title}
            </Text>
            {linkedCaption && (
              <Text size="xs" c="orange.6" lineClamp={1}>
                {linkedCaption}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {timeLabel}
            </Text>
          </Stack>
        </Group>
      </Box>

      {actionBar && (
        <Group spacing="md" sx={FEED_ACTION_BAR_SX} align="center" wrap="nowrap">
          {actionBar}
        </Group>
      )}
    </Card>
  );
}
